import {
  AnalyticsEvent,
  AnalyticsImplementation,
  ConfigApi,
  FeatureFlagsApi,
  IdentityApi,
} from '@backstage/frontend-plugin-api';

/** The slice of the global that `script.js` installs as `window.rybbit`. */
interface RybbitApi {
  event(
    name: string,
    properties?: Record<string, string | number | boolean>,
  ): void;
  identify(userId: string, traits?: Record<string, unknown>): void;
}

declare global {
  interface Window {
    rybbit?: RybbitApi;
  }
}

/**
 * Whether events carry the signed-in user. `required` queues them until the
 * user is known, so a session is never split anonymous then identified.
 */
export type RybbitIdentityMode = 'disabled' | 'optional' | 'required';

const IDENTITY_MODES = ['disabled', 'optional', 'required'] as const;

const readIdentityMode = (value: string | undefined): RybbitIdentityMode => {
  if (value === undefined) {
    return 'disabled';
  }
  if (!(IDENTITY_MODES as readonly string[]).includes(value)) {
    throw new Error(
      `Invalid app.analytics.rybbit.identity '${value}', expected one of ${IDENTITY_MODES.join(
        ', ',
      )}`,
    );
  }
  return value as RybbitIdentityMode;
};

/** Context is an open record whose extra keys may be undefined. */
const definedProperties = (context: AnalyticsEvent['context']) =>
  Object.fromEntries(
    Object.entries(context).filter(([, value]) => value !== undefined),
  ) as Record<string, string | number | boolean>;

/** Rybbit's documented ceiling for `data-tag`. */
const TAG_LIMIT = 256;

/** Drops whole names: a clipped one would read as a different cohort. */
const joinWithinLimit = (names: string[]): string => {
  const render = (count: number) => {
    const parts = names.slice(0, count);
    if (count < names.length) {
      parts.push(`+${names.length - count}`);
    }
    return parts.join(',');
  };

  let kept = names.length;
  while (kept > 0 && render(kept).length > TAG_LIMIT) {
    kept -= 1;
  }
  return render(kept);
};

/**
 * The active flags as one tag, sorted so a given set is always the same
 * cohort. Registered flags only, so a stale localStorage entry for a removed
 * flag cannot invent one.
 */
const activeFlagTag = (featureFlagsApi: FeatureFlagsApi): string =>
  joinWithinLimit(
    featureFlagsApi
      .getRegisteredFlags()
      .map(flag => flag.name)
      .filter(name => featureFlagsApi.isActive(name))
      .sort(),
  );

const noop: AnalyticsImplementation = { captureEvent: () => {} };

type TrackerOptions = {
  analyticsHost: string;
  siteId: string;
  tag?: string;
  debounceDuration?: number;
  skipPatterns?: string[];
  maskPatterns?: string[];
};

/**
 * Injects Rybbit's install snippet, which is why its origin has to be in
 * `backend.csp.script-src`. Not the `@rybbit/js` package: that posts through
 * `sendBeacon`, always a credentialed request, and Rybbit answers the
 * preflight without `Access-Control-Allow-Credentials`.
 */
const loadTracker = (options: TrackerOptions): Promise<RybbitApi> =>
  new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `${options.analyticsHost.replace(/\/$/, '')}/script.js`;
    script.defer = true;
    script.dataset.siteId = options.siteId;
    if (options.tag) {
      script.dataset.tag = options.tag;
    }
    if (options.debounceDuration !== undefined) {
      script.dataset.debounce = String(options.debounceDuration);
    }
    if (options.skipPatterns) {
      script.dataset.skipPatterns = JSON.stringify(options.skipPatterns);
    }
    if (options.maskPatterns) {
      script.dataset.maskPatterns = JSON.stringify(options.maskPatterns);
    }

    script.addEventListener('load', () =>
      window.rybbit
        ? resolve(window.rybbit)
        : reject(new Error('Rybbit script loaded without installing a global')),
    );
    script.addEventListener('error', () =>
      reject(new Error(`Failed to load ${script.src}`)),
    );

    document.head.appendChild(script);
  });

/**
 * Forwards the Backstage Analytics API to Rybbit as custom events named after
 * each action. `navigate` is dropped: the tracker counts pageviews itself and
 * its `pageview()` takes no path, so there is nothing to hand it.
 */
export class RybbitAnalytics implements AnalyticsImplementation {
  private api: RybbitApi | undefined;

  /** Events captured before the tracker, and `identify()`, were ready. */
  private pending: AnalyticsEvent[] | undefined = [];

  private constructor(ready: Promise<RybbitApi>) {
    void ready
      .then(api => {
        this.api = api;
      })
      // Flushed on failure too, where `send` drops them for want of a tracker.
      .catch(() => undefined)
      .then(() => this.flush());
  }

  /**
   * Host and site ID are optional so an app with no site configured is quiet
   * rather than unable to boot.
   */
  static fromConfig(
    config: ConfigApi,
    options: {
      identityApi?: IdentityApi;
      featureFlagsApi?: FeatureFlagsApi;
    } = {},
  ): AnalyticsImplementation {
    const rybbitConfig = config.getOptionalConfig('app.analytics.rybbit');
    const analyticsHost = rybbitConfig?.getOptionalString('analyticsHost');
    const siteId = rybbitConfig?.getOptionalString('siteId');
    if (!rybbitConfig || !analyticsHost || !siteId) {
      return noop;
    }

    const identity = readIdentityMode(
      rybbitConfig.getOptionalString('identity'),
    );

    // Read once, which is all they are worth: extension predicates are
    // evaluated at app build, so toggling a flag needs a reload anyway.
    const { featureFlagsApi } = options;
    const tagFlags = rybbitConfig.getOptionalBoolean('tagFeatureFlags');

    const loaded = loadTracker({
      analyticsHost,
      siteId,
      tag:
        tagFlags && featureFlagsApi
          ? activeFlagTag(featureFlagsApi)
          : undefined,
      debounceDuration: rybbitConfig.getOptionalNumber('debounceDuration'),
      skipPatterns: rybbitConfig.getOptionalStringArray('skipPatterns'),
      maskPatterns: rybbitConfig.getOptionalStringArray('maskPatterns'),
    });

    const { identityApi } = options;
    if (identity === 'disabled' || !identityApi) {
      return new RybbitAnalytics(loaded);
    }

    const identified = loaded.then(async api => {
      const { userEntityRef } = await identityApi.getBackstageIdentity();
      api.identify(userEntityRef);
      return api;
    });

    if (identity === 'required') {
      // Falling back to `loaded` costs attribution rather than the session.
      return new RybbitAnalytics(identified.catch(() => loaded));
    }
    // `optional` sends as soon as the tracker is up, so nothing awaits this.
    void identified.catch(() => undefined);
    return new RybbitAnalytics(loaded);
  }

  captureEvent(event: AnalyticsEvent): void {
    if (this.pending) {
      this.pending.push(event);
      return;
    }
    this.send(event);
  }

  private flush(): void {
    const queued = this.pending ?? [];
    this.pending = undefined;
    queued.forEach(event => this.send(event));
  }

  private send(event: AnalyticsEvent): void {
    const { action, subject, value, attributes, context } = event;
    if (action === 'navigate' || !this.api) {
      return;
    }

    // Later keys win: attributes beat context, subject and value beat both.
    this.api.event(action, {
      ...definedProperties(context),
      ...attributes,
      subject,
      ...(value === undefined ? {} : { value }),
    });
  }
}
